import { Thermometer } from 'lucide-react';
import { useCardStyles } from './cardStyles';
import { attrNumber, friendlyName, useEntity } from './useEntity';

export interface ClimateCardProps {
  entityId: string;
  title?: string;
}

const MODE_STYLES: Record<string, { label: string; bg: string; text: string }> = {
  heat: { label: 'Heizen', bg: 'bg-rose-500/20', text: 'text-rose-500 dark:text-rose-400' },
  cool: { label: 'Kühlen', bg: 'bg-sky-500/20', text: 'text-sky-500 dark:text-sky-400' },
  auto: { label: 'Auto', bg: 'bg-emerald-500/20', text: 'text-emerald-500 dark:text-emerald-400' },
  heat_cool: { label: 'Auto', bg: 'bg-emerald-500/20', text: 'text-emerald-500 dark:text-emerald-400' },
  dry: { label: 'Trocknen', bg: 'bg-amber-500/20', text: 'text-amber-500 dark:text-amber-400' },
  fan_only: { label: 'Lüfter', bg: 'bg-indigo-500/20', text: 'text-indigo-500 dark:text-indigo-400' },
  off: { label: 'Aus', bg: 'bg-gray-500/20', text: 'text-gray-500 dark:text-gray-400' },
};

export function ClimateCard({ entityId, title }: ClimateCardProps): JSX.Element {
  const styles = useCardStyles();
  const entity = useEntity(entityId);
  const currentTemp = attrNumber(entity, 'current_temperature');
  const targetTemp = attrNumber(entity, 'temperature');
  const mode = entity?.state ?? 'off';
  const modeStyle = MODE_STYLES[mode] ?? MODE_STYLES.off;

  return (
    <div
      className={styles.container}
      data-domain="climate"
      data-entity-id={entityId}
      data-state={mode}
    >
      <div className="flex items-center justify-between">
        <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${modeStyle.bg} ${modeStyle.text}`}>
          <Thermometer className="h-5 w-5" />
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${modeStyle.bg} ${modeStyle.text}`}>
          {modeStyle.label}
        </span>
      </div>

      <div>
        <div className={`truncate text-xs uppercase tracking-wider ${styles.label}`}>
          {friendlyName(entity, title)}
        </div>
        <div className={`mt-1 flex items-baseline gap-2 ${styles.title}`}>
          <span className="text-3xl font-semibold tabular-nums">
            {targetTemp !== undefined ? targetTemp.toFixed(1) : '—'}
          </span>
          <span className={`text-sm ${styles.muted}`}>°C Ziel</span>
        </div>
      </div>

      {currentTemp !== undefined && (
        <div className={`text-xs ${styles.subtle}`}>
          Aktuell{' '}
          <span className={`font-medium ${styles.muted} tabular-nums`}>
            {currentTemp.toFixed(1)} °C
          </span>
        </div>
      )}
    </div>
  );
}
